import React, { useCallback, useEffect, useState } from 'react';

import { Box } from '@mui/material';

import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  selectComparison,
  selectSlowdownThreshold,
  setComparison,
  setSlowdownThreshold,
} from 'state/workflowSlice';

import { StlPopup } from 'features/common/StlPopup/StlPopup';
import { ComparisonPicker } from 'features/task_bar/ComparisonPicker';
import { SlowdownThresholdSlider } from 'features/task_bar/SlowdownThresholdSlider';

import styles from './RouteMonitoringSettings.module.css';

interface Props {
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
  onApply?: () => void;
}

interface InitialValues {
  comparison: string | undefined;
  slowdownThreshold: number | undefined;
}

export default function RouteMonitoringSettings({
  isOpen,
  setIsOpen,
  onApply,
}: Props) {
  const dispatch = useAppDispatch();
  const [initialValues, setInitialValues] = useState<InitialValues>({
    comparison: undefined,
    slowdownThreshold: undefined,
  });

  const comparison = useAppSelector(selectComparison);
  const slowdownThreshold = useAppSelector(selectSlowdownThreshold);

  useEffect(() => {
    if (isOpen) setInitialValues({ comparison, slowdownThreshold });
  }, [isOpen]);

  const cancel = useCallback(() => {
    setIsOpen(false);
    if (initialValues.comparison !== undefined)
      dispatch(setComparison(initialValues.comparison));
    if (initialValues.slowdownThreshold !== undefined)
      dispatch(setSlowdownThreshold(initialValues.slowdownThreshold));
  }, [dispatch, initialValues]);

  return (
    <>
      <StlPopup
        title="Modify Comparison Settings"
        isOpen={isOpen}
        onCancel={cancel}
        onClose={() => setIsOpen(false)}
        onApply={onApply}
        sx={{ minWidth: '30rem' }}
      >
        <Box className={styles.settingsBox}>
          <ComparisonPicker radio forceMonthOnly inline />
          <SlowdownThresholdSlider inline />
        </Box>
      </StlPopup>
      <div style={{ display: 'none' }}>
        {/* Settings are here hidden so that they auto-populate with available days/months without having to open settings */}
        <ComparisonPicker radio forceMonthOnly />
      </div>
    </>
  );
}
